import type { ServiceLeadServiceType } from '@/data/service-quote-funnels'
import type { DiagnosisIdentification } from '@/lib/types/diagnosis'

// CJ Affiliate (Commission Junction) tracking for the Angi advertiser
// (7439025). Property: "What Grass Is This? Lawn Care Tools & Guides"
// (PID 101814843). Click URLs follow https://www.tkqlhce.com/click-<PID>-<linkId>
// and land on Angi's service-request funnel for the mapped category; commissions
// pay per completed service request. Link IDs pulled from the CJ links list
// 2026-07-18 — only three Angi categories map cleanly onto our service types.
const CJ_PID = '101814843'

const ANGI_LINK_IDS: Record<ServiceLeadServiceType, string> = {
  // Angi "Lawn & Garden Care" (17142834)
  lawn_care: '17142834',
  weed_control: '17142834',
  sprinkler_repair: '17142834',
  landscaping: '17142834',
  tree_removal: '17142834',
  sod_installation: '17142834',
  aeration_overseeding: '17142834',
  mulch_installation: '17142834',
  // Angi "Pest Control Services" (17141195)
  pest_control: '17141195',
  mosquito_control: '17141195',
  // Angi "Electrical" (17142831)
  outdoor_lighting: '17142831',
}

/**
 * Diagnose problem category -> Angi service. Pests get Angi's dedicated Pest
 * Control funnel; everything else lands on Lawn & Garden Care (there is no
 * disease/nutrient-specific Angi category).
 */
export const DIAGNOSIS_TYPE_TO_SERVICE: Record<
  DiagnosisIdentification['type'],
  ServiceLeadServiceType
> = {
  pest: 'pest_control',
  disease: 'lawn_care',
  weed: 'weed_control',
  nutrient_deficiency: 'lawn_care',
  environmental: 'lawn_care',
}

/**
 * Longest sid we send. CJ truncates longer sub-IDs in its reports, so the cap
 * sits under CJ's limit and keeps the placement intact.
 */
export const CJ_SID_MAX = 60
/** The placement keeps up to this many characters; the page slug gets the rest. */
const CJ_SID_PLACEMENT_MAX = 32
/** Joins placement and page slug. Placements use single underscores, slugs hyphens. */
export const CJ_SID_SEPARATOR = '__'

/** Lowercase `[a-z0-9_-]` only, runs of anything else collapsed to one hyphen. */
function sanitizeSidPart(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^[-_]+|[-_]+$/g, '')
}

/**
 * CJ sub-ID: `<placement>__<page-slug>`, sanitized to a charset CJ reports
 * keep verbatim and capped at CJ_SID_MAX. The placement is truncated first to
 * CJ_SID_PLACEMENT_MAX so a long slug can never push it out; the slug fills
 * what is left. A path is reduced to its last segment (`/blog/foo` -> `foo`).
 * Slugs only: never pass a query string or anything user-typed.
 */
export function buildAngiSid(placement?: string, pageSlug?: string): string {
  const place = sanitizeSidPart(placement ?? '').slice(0, CJ_SID_PLACEMENT_MAX).replace(/[-_]+$/, '')
  const lastSegment = (pageSlug ?? '').split(/[?#]/)[0].split('/').filter(Boolean).pop() ?? ''
  const slug = sanitizeSidPart(lastSegment)
  if (!slug) return place.slice(0, CJ_SID_MAX)
  if (!place) return slug.slice(0, CJ_SID_MAX).replace(/[-_]+$/, '')
  const room = CJ_SID_MAX - place.length - CJ_SID_SEPARATOR.length
  if (room <= 0) return place.slice(0, CJ_SID_MAX)
  const slugPart = slug.slice(0, room).replace(/[-_]+$/, '')
  return slugPart ? `${place}${CJ_SID_SEPARATOR}${slugPart}` : place
}

/**
 * Tracked Angi click URL for a service type. `sid` is CJ's per-click
 * sub-ID (shows up in commission reports): pass the placement, and the page
 * slug when the surface has one, so earnings split by surface and by page.
 * Keep both PII-free.
 */
export function getAngiAffiliateUrl(
  serviceType: ServiceLeadServiceType,
  placement?: string,
  pageSlug?: string,
): string {
  const base = `https://www.tkqlhce.com/click-${CJ_PID}-${ANGI_LINK_IDS[serviceType]}`
  const sid = buildAngiSid(placement, pageSlug)
  return sid ? `${base}?sid=${encodeURIComponent(sid)}` : base
}
